import { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Trophy, Skull, TrendingUp, Calendar, LogOut } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import PullToRefresh from '../components/PullToRefresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StatsPage({ currentMember, onLogout }) {
  const [stats, setStats] = useState({});
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/stats/attendance`),
        axios.get(`${API}/events`)
      ]);
      
      setStats(statsRes.data);
      setEvents(eventsRes.data);
      return true;
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load stats');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getPastEvents = () => {
    const now = new Date();
    return events.filter(event => {
      const eventDate = new Date(`${event.date}T${event.time}`);
      return eventDate < now;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const getLeaderboard = () => {
    return Object.entries(stats)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.attended - a.attended);
  };

  const getShameList = () => {
    return Object.entries(stats)
      .map(([name, data]) => ({ name, ...data }))
      .filter(item => item.no_shows > 0)
      .sort((a, b) => b.no_shows - a.no_shows);
  };

  const pastEvents = getPastEvents();
  const leaderboard = getLeaderboard();
  const shameList = getShameList();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050203] flex items-center justify-center">
        <Trophy className="w-12 h-12 text-[#E11D48] animate-pulse" />
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={fetchData}>
      <div className="min-h-screen bg-[#050203] relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'url(https://www.transparenttextures.com/patterns/black-felt.png)',
        }}
      />

      <div className="max-w-md mx-auto min-h-screen relative shadow-2xl border-x border-white/5">
        {/* Header */}
        <div className="bg-zinc-900/50 backdrop-blur-sm border-b border-white/10 p-6 sticky top-0 z-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/" className="text-zinc-400 hover:text-[#E11D48] transition-colors">
                <ArrowLeft className="w-6 h-6" />
              </Link>
              <div>
                <h1 className="font-['Anton'] text-2xl text-white uppercase tracking-tight">
                  STATS
                </h1>
                <p className="text-zinc-500 text-xs uppercase tracking-widest">
                  {currentMember}
                </p>
              </div>
            </div>
            <button
              onClick={onLogout}
              data-testid="logout-button"
              className="p-2 text-zinc-400 hover:text-[#E11D48] transition-colors"
            >
              <LogOut className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Leaderboard */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Trophy className="w-8 h-8 text-[#E11D48]" />
              <h2 className="font-['Anton'] text-3xl text-white uppercase tracking-tight">
                MOST COMMITTED FREAKS
              </h2>
            </div>
            <div className="space-y-3">
              {leaderboard.map((member, index) => (
                <div
                  key={member.name}
                  className={`bg-zinc-900/50 border ${
                    index === 0 ? 'border-[#E11D48]' : 'border-white/10'
                  } p-4 flex items-center justify-between`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`text-2xl font-['Anton'] ${
                        index === 0 ? 'text-[#E11D48]' : 'text-zinc-500'
                      }`}
                    >
                      #{index + 1}
                    </div>
                    <div>
                      <div className="text-white font-bold text-lg">
                        {member.name}
                        {index === 0 && ' 👑'}
                      </div>
                      <div className="text-zinc-400 text-sm">
                        {member.attended} / {member.total_past_events} events
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[#E11D48] font-['Anton'] text-2xl">
                      {member.attendance_rate}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Shame List */}
          {shameList.length > 0 && (
            <div
              className="bg-zinc-900 p-6 rotate-1 shadow-xl border-2 border-white/5"
              style={{
                backgroundImage: 'url(https://www.transparenttextures.com/patterns/black-felt.png)',
              }}
            >
              <div className="flex items-center gap-3 mb-6">
                <Skull className="w-8 h-8 text-red-500" />
                <h2 className="font-['Anton'] text-3xl text-red-500 uppercase tracking-tight">
                  FUCK 'EM HALL OF SHAME
                </h2>
              </div>
              <div className="space-y-4">
                {shameList.map((member) => (
                  <div
                    key={member.name}
                    className="flex items-center justify-between border-b border-red-900/30 pb-3"
                  >
                    <div className="font-['Anton'] text-2xl text-red-500 line-through decoration-white/30 decoration-2">
                      🖕🏼 {member.name}
                    </div>
                    <div className="text-zinc-400 text-sm">
                      {member.no_shows} no-show{member.no_shows > 1 ? 's' : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Past Events */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Calendar className="w-8 h-8 text-[#E11D48]" />
              <h2 className="font-['Anton'] text-3xl text-white uppercase tracking-tight">
                PAST EVENTS
              </h2>
            </div>
            {pastEvents.length > 0 ? (
              <div className="space-y-3">
                {pastEvents.map((event) => (
                  <div
                    key={event.id}
                    className="bg-zinc-900/30 border border-white/10 p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-['Anton'] text-xl text-white uppercase">
                          {event.title}
                        </h3>
                        <div className="text-zinc-400 text-sm mt-1">
                          {new Date(event.date).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                          {' • '}
                          {event.location}
                        </div>
                      </div>
                      <div className="font-['JetBrains_Mono'] text-xl text-[#E11D48]/50">
                        #{event.event_number}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-zinc-500">
                <p>No past events yet</p>
                <p className="text-sm mt-2">Get the party started!</p>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </PullToRefresh>
  );
}

export default StatsPage;
